import axios from "axios";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { Button } from "../../components/Button";
import { Card } from "../../components/Card";
import { NotificationButton } from "../../components/NotificationButton";
import { SelectInput } from "../../components/SelectInput/SelectInput";
import { SUPPORT_EMAIL } from "../../env";
import { api } from "../../services/api";
import { useApp } from "../../services/app-context";
import { useUser } from "../../services/user-context";

const DEFAULT_ISSUE_OPTIONS = [
  "Something isn't working",
  "Page won't load or crashes",
  "AI feedback is incorrect",
  "Certification issue",
  "Other",
];

const DEFAULT_FEEDBACK_OPTIONS = [
  "PraxisPlay experience",
  "PraxisPAL experience",
  "PraxisCertify",
  "Badge Center",
  "Dashboard & Insights",
  "Other",
];

const toSelectOptions = (strings: string[]) =>
  strings.map((s) => ({ value: s, label: s }));

const PANEL_CARD_CLASS = "flex-1 !px-[18px] !py-[13px] flex flex-col";

type ActiveCard = "bug" | "bug-success" | "feedback" | "feedback-success" | null;

function PanelHeader({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div>
      <div className="text-black text-lg font-semibold mb-1">{title}</div>
      <div className="text-black text-xs font-normal">{subtitle}</div>
    </div>
  );
}

function ErrorBanner({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  return (
    <div className="flex items-center justify-between bg-[#fff0f0] border border-red-200 rounded-[6px] px-3 py-2.5">
      <span className="text-red-600 text-xs">{message}</span>
      <button
        type="button"
        onClick={onDismiss}
        className="ml-2 text-red-400 hover:text-red-600 flex-shrink-0"
        aria-label="Dismiss error"
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  );
}

const selectClass = (hasError: boolean) =>
  hasError
    ? "rounded-[6px] text-xs py-2 !border !border-red-500 !bg-red-50"
    : "bg-[#f4f4f4] border-0 rounded-[6px] text-xs py-2";

const textareaClass = (hasError: boolean) =>
  hasError
    ? "rounded-[6px] p-3 text-xs placeholder-[#757575] text-black resize-none outline-none w-full border border-red-500 bg-red-50"
    : "bg-[#f4f4f4] rounded-[6px] p-3 text-xs placeholder-[#757575] text-black resize-none outline-none w-full";

export function Support() {
  const { tenantProfile } = useApp();
  const { userInfo } = useUser() as any;
  const supportEmail = tenantProfile?.supportEmail || SUPPORT_EMAIL;
  const rawIssues = tenantProfile?.get_in_touch?.report_a_bug?.list_of_issues;
  const rawTopics = tenantProfile?.get_in_touch?.share_feedback?.list_of_topics;
  const issueOptions = toSelectOptions(rawIssues?.length ? rawIssues : DEFAULT_ISSUE_OPTIONS);
  const feedbackOptions = toSelectOptions(rawTopics?.length ? rawTopics : DEFAULT_FEEDBACK_OPTIONS);

  const [activeCard, setActiveCard] = useState<ActiveCard>(null);
  const activeCardRef = useRef<ActiveCard>(null);
  useEffect(() => { activeCardRef.current = activeCard; }, [activeCard]);

  // Bug form
  const [bugIssueType, setBugIssueType] = useState("");
  const [bugDescription, setBugDescription] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [bugError, setBugError] = useState(false);
  const [isSubmittingBug, setIsSubmittingBug] = useState(false);

  // Feedback form
  const [feedbackTopic, setFeedbackTopic] = useState("");
  const [feedbackDescription, setFeedbackDescription] = useState("");
  const [feedbackError, setFeedbackError] = useState(false);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);

  const handleEmail = () => {
    window.location.href = `mailto:${supportEmail}`;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const incoming = Array.from(e.target.files);
      setFiles((prev) => [...prev, ...incoming].slice(0, 5));
      e.target.value = "";
    }
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const resetBugForm = () => {
    setBugIssueType("");
    setBugDescription("");
    setFiles([]);
    setBugError(false);
  };

  const resetFeedbackForm = () => {
    setFeedbackTopic("");
    setFeedbackDescription("");
    setFeedbackError(false);
  };

  const handleBugSubmit = async () => {
    if (!bugIssueType || !bugDescription.trim()) {
      setBugError(true);
      return;
    }
    setBugError(false);
    setIsSubmittingBug(true);

    try {
      // Stage 1: Create the Jira ticket. Failure here aborts — nothing was created yet.
      let jiraKey: string;
      try {
        const { data } = await api.submitUserReport({
          title: bugIssueType,
          description: bugDescription.trim(),
          type: "bug",
          browserInfo: navigator.userAgent,
          osInfo: navigator.platform,
          currentUrl: window.location.href,
        });
        jiraKey = data.jiraKey;
      } catch {
        toast.error("Failed to submit report. Please try again.");
        return;
      }

      // Stage 2: Upload attachments. The ticket already exists, so failures here are
      // non-fatal — use allSettled so one bad file doesn't discard successfully uploaded ones.
      if (files.length > 0) {
        const results = await Promise.allSettled(
          files.map(async (file) => {
            const { data: urlData } = await api.getAttachmentUploadUrl({
              filename: file.name,
              mimeType: file.type || "application/octet-stream",
            });
            await axios.put(urlData.uploadUrl, file, {
              headers: { "Content-Type": file.type || "application/octet-stream" },
            });
            return urlData.key;
          })
        );

        const successfulKeys = results
          .filter((r): r is PromiseFulfilledResult<string> => r.status === "fulfilled")
          .map((r) => r.value);

        if (successfulKeys.length > 0) {
          try {
            await api.attachFilesToReport(jiraKey, successfulKeys);
          } catch {
            toast.error("Report submitted, but attachments could not be linked to the ticket. You can email them directly.");
          }
        }

        const failedCount = results.length - successfulKeys.length;
        if (failedCount > 0) {
          toast.error(
            `Report submitted, but ${failedCount} file${failedCount > 1 ? "s" : ""} failed to upload. You can email them directly.`
          );
        }
      }

      resetBugForm();
      if (activeCardRef.current === "bug") {
        setActiveCard("bug-success");
      }
    } finally {
      setIsSubmittingBug(false);
    }
  };

  const handleFeedbackSubmit = async () => {
    if (!feedbackTopic || !feedbackDescription.trim()) {
      setFeedbackError(true);
      return;
    }
    setFeedbackError(false);
    setIsSubmittingFeedback(true);
    try {
      await api.submitUserReport({
        title: feedbackTopic,
        description: feedbackDescription.trim(),
        type: "feedback",
        browserInfo: navigator.userAgent,
        osInfo: navigator.platform,
        currentUrl: window.location.href,
      });

      resetFeedbackForm();
      if (activeCardRef.current === "feedback") {
        setActiveCard("feedback-success");
      }
    } catch {
      toast.error("Failed to submit feedback. Please try again.");
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  const cardBase = "border-2 border-[#ececec] !px-[18px] !py-[13px] transition-colors";
  const cardHover = "hover:bg-background2 hover:border-primary";
  const cardActiveStyle = "!bg-background2 !border-primary";

  const isBugActive = activeCard === "bug" || activeCard === "bug-success";
  const isFeedbackActive = activeCard === "feedback" || activeCard === "feedback-success";

  return (
    <div className="p-6 flex flex-col gap-4 h-full">
      <div className="flex items-start justify-between">
        <div className="flex flex-col gap-1">
          <div className="text-black text-[34px] font-bold leading-tight">Get In Touch</div>
          <div className="text-gray3 text-base font-normal">Need help? We&apos;re here.</div>
        </div>
        <NotificationButton />
      </div>

      <div className="flex gap-6 items-stretch flex-1 min-h-0">
        <div className="flex flex-col gap-4 w-[480px] shrink-0 self-start">
          <Card className={`${cardBase} ${cardHover}`}>
            <div className="text-black text-lg font-semibold mb-2">Contact Us</div>
            <div className="text-black text-xs font-normal mb-4">
              Feel free to reach out with any questions{" "}
              <a href={`mailto:${supportEmail}`} className="font-medium underline">
                {supportEmail}
              </a>
            </div>
            <Button
              onClick={handleEmail}
              variant="primary"
              className="!py-1.5 !px-3 !rounded-full !text-xs !h-auto"
            >
              Email Support
            </Button>
          </Card>

          <Card className={`${cardBase} ${isBugActive ? cardActiveStyle : cardHover}`}>
            <div className="text-black text-lg font-semibold mb-2">Report a Bug</div>
            <div className="text-black text-xs font-normal mb-4">
              Something not working as expected? Let us know!
            </div>
            <Button
              onClick={() => setActiveCard(isBugActive ? null : "bug")}
              variant="primary"
              className="!py-1.5 !px-3 !rounded-full !text-xs !h-auto"
            >
              Report Bug
            </Button>
          </Card>

          <Card className={`${cardBase} ${isFeedbackActive ? cardActiveStyle : cardHover}`}>
            <div className="text-black text-lg font-semibold mb-2">Share Feedback</div>
            <div className="text-black text-xs font-normal mb-4">
              Ideas, suggestions, or product feedback.
            </div>
            <Button
              onClick={() => setActiveCard(isFeedbackActive ? null : "feedback")}
              variant="primary"
              className="!py-1.5 !px-3 !rounded-full !text-xs !h-auto"
            >
              Send Feedback
            </Button>
          </Card>
        </div>

        {/* Bug Report Form */}
        {activeCard === "bug" && (
          <Card className={`${PANEL_CARD_CLASS} gap-4`}>
            <PanelHeader
              title="Report a Bug"
              subtitle="Something not working as expected? Let us know!"
            />

            <div className="flex flex-col gap-1.5">
              <label className="text-black text-xs font-medium">Issue:</label>
              <SelectInput
                value={bugIssueType}
                onChange={(e) => setBugIssueType(e.target.value)}
                options={issueOptions}
                placeholder="Select an issue"
                containerClassName="w-full"
                className={selectClass(bugError && !bugIssueType)}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-black text-xs font-medium">Description:</label>
              <textarea
                value={bugDescription}
                onChange={(e) => setBugDescription(e.target.value)}
                placeholder="Tell us more..."
                rows={8}
                className={textareaClass(bugError && !bugDescription.trim())}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <div className="text-black text-xs font-medium">
                Screenshots help us resolve issues faster:
              </div>
              <label className="flex items-center gap-2 bg-[#f4f4f4] rounded-[6px] px-3 py-2.5 cursor-pointer w-fit">
                <svg
                  width="17"
                  height="17"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#333"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                </svg>
                <span className="text-black text-xs font-normal">Attach an image or video</span>
                <input
                  type="file"
                  accept="image/*,video/*"
                  multiple
                  className="hidden"
                  onChange={handleFileChange}
                />
              </label>
              {files.length > 0 && (
                <div className="flex flex-col gap-1 mt-1">
                  {files.map((file, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between bg-[#f4f4f4] rounded-[6px] px-3 py-2"
                    >
                      <span className="text-black text-xs truncate max-w-[240px]">{file.name}</span>
                      <button
                        type="button"
                        onClick={() => removeFile(idx)}
                        className="ml-2 text-[#757575] hover:text-red-500 flex-shrink-0"
                        aria-label="Remove file"
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                          <path d="M10 11v6" />
                          <path d="M14 11v6" />
                          <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {bugError && (
              <ErrorBanner
                message="Failed to report bug. Please fill all sections."
                onDismiss={() => setBugError(false)}
              />
            )}

            <div className="flex items-end justify-between pt-2 mt-auto">
              <div className="flex flex-col gap-0.5">
                <span className="text-[#757575] text-[10px]">
                  App Version: {process.env.REACT_APP_VERSION || "--"}
                </span>
                <span className="text-[#757575] text-[10px]">
                  Account: {userInfo?.email || "--"}
                </span>
              </div>
              <Button
                onClick={handleBugSubmit}
                loading={isSubmittingBug}
                disabled={isSubmittingBug}
                variant="primary"
                className="!py-1.5 !px-3 !rounded-full !text-xs !h-auto"
              >
                Report Bug
              </Button>
            </div>
          </Card>
        )}

        {/* Bug Success */}
        {activeCard === "bug-success" && (
          <Card className={`${PANEL_CARD_CLASS} gap-3`}>
            <PanelHeader
              title="Report a Bug"
              subtitle="Something not working as expected? Let us know!"
            />
            <div className="flex flex-col gap-3">
              <span className="text-black text-[13px] font-medium">Thank you!</span>
              <p className="text-black text-xs font-normal">
                We&apos;ve received your feedback. Thank you for helping make PraxisPro better.
              </p>
              <Button
                onClick={() => {
                  resetBugForm();
                  setActiveCard("bug");
                }}
                variant="primary"
                className="!py-1.5 !px-3 !rounded-full !text-xs !h-auto w-fit"
              >
                Report a new bug
              </Button>
            </div>
          </Card>
        )}

        {/* Feedback Form */}
        {activeCard === "feedback" && (
          <Card className={`${PANEL_CARD_CLASS} gap-4`}>
            <PanelHeader
              title="Share Feedback"
              subtitle="Ideas, suggestions, or product feedback."
            />

            <div className="flex flex-col gap-1.5">
              <label className="text-black text-xs font-medium">Topic:</label>
              <SelectInput
                value={feedbackTopic}
                onChange={(e) => setFeedbackTopic(e.target.value)}
                options={feedbackOptions}
                placeholder="Select a topic"
                containerClassName="w-full"
                className={selectClass(feedbackError && !feedbackTopic)}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-black text-xs font-medium">Description:</label>
              <textarea
                value={feedbackDescription}
                onChange={(e) => setFeedbackDescription(e.target.value)}
                placeholder="Tell us more..."
                rows={8}
                className={textareaClass(feedbackError && !feedbackDescription.trim())}
              />
            </div>

            {feedbackError && (
              <ErrorBanner
                message="Please fill all sections."
                onDismiss={() => setFeedbackError(false)}
              />
            )}

            <div className="flex items-end justify-between pt-2 mt-auto">
              <span className="text-[#757575] text-[10px]">
                Account: {userInfo?.email || "--"}
              </span>
              <Button
                onClick={handleFeedbackSubmit}
                loading={isSubmittingFeedback}
                disabled={isSubmittingFeedback}
                variant="primary"
                className="!py-1.5 !px-3 !rounded-full !text-xs !h-auto"
              >
                Send Feedback
              </Button>
            </div>
          </Card>
        )}

        {/* Feedback Success */}
        {activeCard === "feedback-success" && (
          <Card className={`${PANEL_CARD_CLASS} gap-3`}>
            <PanelHeader
              title="Share Feedback"
              subtitle="Ideas, suggestions, or product feedback."
            />
            <div className="flex flex-col gap-3">
              <span className="text-black text-[13px] font-medium">Thank you!</span>
              <p className="text-black text-xs font-normal">
                Thanks for the feedback. Our product team reviews every submission.
              </p>
              <Button
                onClick={() => {
                  resetFeedbackForm();
                  setActiveCard("feedback");
                }}
                variant="primary"
                className="!py-1.5 !px-3 !rounded-full !text-xs !h-auto w-fit"
              >
                Share more feedback
              </Button>
            </div>
            <div className="mt-auto pt-2">
              <span className="text-[#757575] text-[10px]">
                Account: {userInfo?.email || "--"}
              </span>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
